<template>
  <div class="container create-event">
    <div
      class="row mb-45"
    >
      <div class="col-8">
        <h1>Create Event</h1>
      </div>
      <div class="col-4 text-right">
        <button
          type="button"
          @click="doSave()"
          class="btn btn-primary profile-button btn-wide create-button"
        >
          <font-awesome-icon
            v-if="saving"
            :spin="true"
            :icon="['fas', 'spinner']"
            class="icon alt"
          />
          <span v-else>Save</span>
        </button>
      </div>
    </div>
    <div class="row">
      <div class="col-12 col-lg-8">
        <div class="dashboard-card event-card">
          <label class="upper">EVENT DETAILS</label>
          <form-component>
            <form
              novalidate="novalidate"
              @submit.prevent="doSave"
            >
              <div class="form-row" v-if="$store.getters.currentUser.isAdmin">
                <div class="col-12">
                  <input-type-ahead
                    :list="colleges"
                    :value="college"
                    :searchchange="getColleges"
                    :selected-value="selectedCollege"
                    placeholder="Search for a College..."
                    label="College"
                    data-vv-name="college"
                    data-vv-as="college"
                    v-validate="'required'"
                    required
                    :error="errors.first('college')"
                    option-value="id"
                    option-text="display_name"
                  />
                </div>
              </div>

              <div class="form-row" v-if="$store.getters.currentUser.isAdmin || $store.getters.currentUser.isCollegeAdmin">
                <div class="col-12">
                  <input-type-ahead
                    :list="users"
                    :value="user"
                    :searchchange="getUsers"
                    :selected-value="selectedUser"
                    placeholder="Search for a User..."
                    label="Host"
                    option-value="id"
                    option-text="display_label"
                  />
                </div>
              </div>

              <div class="form-row">
                <div class="col-12">
                  <input-text
                    id="topic"
                    v-model="topic"
                    v-validate="'required'"
                    name="topic"
                    data-vv-name="topic"
                    data-vv-as="topic"
                    label="Topic"
                    type="text"
                    required
                    :error="errors.first('topic')"
                  />
                </div>
              </div>

              <input-single-date-time-range-picker
                ref="dateRangePicker" />

              <div class="form-row">
                <div class="col-12">
                  <input-text-area
                    id="description"
                    v-model="description"
                    v-validate="'required'"
                    name="description"
                    data-vv-name="description"
                    data-vv-as="description"
                    label="Description"
                    type="text"
                    required
                    :error="errors.first('description')"
                  />
                </div>

                <div class="col-12">
                  <label class="replacement-label">RELATED TOPICS/CATEGORIES</label>
                  <vue-tags-input
                    v-model="specialty"
                    placeholder="Select Topics"
                    :add-only-from-autocomplete="true"
                    :autocomplete-items="filteredSpecialties"
                    :autocomplete-min-length="0"
                    :tags="specialty_ids"
                    @tags-changed="newTags => specialty_ids = newTags"
                  />                 
                </div>
              </div>
            </form>
          </form-component>
        </div>
      </div>
      <div class="col-lg-4">
        <div class="dashboard-card event-settings">
          <label>EVENT SETTINGS</label>
          <form-component>
            <input-text
              id="user_limit"
              v-model="user_limit"
              name="user_limit"
              label="Maximum Attendees"
              type="number"
            />

            <input-select
              id="in_person"
              v-model="in_person"
              name="in_person"
              label="In Person"
              type="checkbox"
              :options="[{ id: 'online', display_label: 'Online' },{ id: 'in-person', display_label: 'In Person' }]"
            />

            <input-text
              v-if="in_person === 'in-person'"
              id="in_person_location"
              v-model="in_person_location"
              name="in_person_location"
              label="Location"
              type="text"
              :error="errors.first('in_person_location')"
            />
          </form-component>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FormComponent from '@/components/FormComponent.vue';
import InputText from '@/components/inputs/InputText.vue';
import InputSelect from '@/components/inputs/InputSelect.vue';
import InputTypeAhead from '@/components/inputs/InputTypeAhead.vue';
import InputTextArea from '@/components/inputs/InputTextArea.vue';
import InputSingleDateTimeRangePicker from '@/components/inputs/InputSingleDateTimeRangePicker.vue';
import VueTagsInput from '@johmun/vue-tags-input';
import CounselorClient from '@/api/CounselorClient';
import UserClient from '@/api/UserClient';
import AdminClient from '@/api/AdminClient';

export default {
  name: 'CreateEvent',
  components: {
    FormComponent,
    InputText,
    InputSelect,
    InputTypeAhead,
    InputTextArea,
    VueTagsInput,
    InputSingleDateTimeRangePicker,
  },
  data() {
    return {
      saving: false,
      counselorClient: new CounselorClient(this.$http),
      adminClient: new AdminClient(this.$http),
      userClient: new UserClient(this.$http),

      topic: '',
      user_limit: '',

      description: '',

      specialty: '',
      specialty_ids: [],
      all_specialties: [],

      in_person: 'online',
      in_person_location: '',


      // typeahead
      colleges: [], // { id, name, state_code }
      _getCollegesTimeout: null,
      college: null,

      users: [], // { id, name, state_code }
      _getUsersTimeout: null,
      user: null,
    };
  },
  computed: {
    filteredSpecialties() {
      return this.all_specialties.filter(i => {
        return i.text.toLowerCase().indexOf(this.specialty.toLowerCase()) !== -1;
      }).map(r => {
        return {
          id: r.id,
          text: r.text,
        };
      });
    },
  },
  async mounted() {
    const all_specialties = await this.userClient.getSpecialtiesList('', 500);
    this.all_specialties = all_specialties.results || [];
  },
  methods: {
    // typeahead
    getColleges(query, done) {
      if (this.college && !query) {
        // we just selected one, do not empty the results
        return;
      }

      if (this._getCollegesTimeout) {
        clearTimeout(this._getCollegesTimeout);
        this._getCollegesTimeout = null;
      }

      this._getCollegesTimeout = setTimeout(() => {
        this.userClient.getCollegeList(query).then((colleges) => {
          this.college = null;
          this.colleges = colleges.results || [];
        }).catch(() => {
          this.college = null;
          this.colleges = [];
        });
      }, 300);
    },
    selectedCollege(v) {
      this.college = v;
    },

    getUsers(query, done) {
      if (this.user && !query) {
        // we just selected one, do not empty the results
        return;
      }

      if (this._getUsersTimeout) {
        clearTimeout(this._getUsersTimeout);
        this._getUsersTimeout = null;
      }

      this._getUsersTimeout = setTimeout(() => {

        if (this.$store.getters.currentUser.isAdmin) {
          this.adminClient.getAllUserList(query).then((users) => {
            this.user = null;
            this.users = users.results || [];
          }).catch(() => {
            this.user = null;
            this.users = [];
          });
        } else {
          this.adminClient.getMyCollegeUserList(query).then((users) => {
            this.user = null;
            this.users = users.results || [];
          }).catch(() => {
            this.user = null;
            this.users = [];
          });
        }
      }, 300);
    },
    selectedUser(v) {
      this.user = v;
    },

    doSave() {
      this.$validator.validateAll().then(async (result) => {
        if (result) {
          this.errors.clear();

          const validDate = await this.$refs.dateRangePicker.validate();

          if (!validDate) {
            return;
          } else {
            this.$refs.dateRangePicker.errors.clear();
          }

          const { start_time, end_time } = this.$refs.dateRangePicker.getDateRange();

          this.saving = true;

          const user_limit = parseInt(this.user_limit, 0) || 0;
          const data = {
            description: this.description,
            topic: this.topic,
            user_limit,
            start_time,
            end_time,
            struggle_area_ids: this.specialty_ids.map(s => s.id),
            in_person: this.in_person === 'in-person',
            in_person_location: this.in_person_location,
          };

          if (this.$store.getters.currentUser.isAdmin) {
            data.school = this.college;
          }

          if (this.user) {
            data.host = this.user;
          }

          this.counselorClient.createGroupEvent(data).then(res => {
            this.saving = false;
            this.$router.push({ name: 'events', params: { from: 'new-event' } });
          }).catch(ex => {             
            this.saving = false;
            let errorMessage = 'Failed to add event. Please try again!';

            if (ex && ex.body && ex.body.error && ex.body.error.extra && ex.body.error.extra.detail) {
              errorMessage = ex.body.error.extra.detail;
            } else if (ex && ex.body && ex.body.error && ex.body.error.message) {
              errorMessage = ex.body.error.message;
            }

            this.$store.commit('setGlobalAlert', {
              msg: errorMessage, error: true,
            });
          })
        }
      });
    },
  },
  watch: {
    start_time(v) {
      if (v && !this.end_time) {
        this.end_time = v;
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.create-event {
  label.upper {
    @include label;

    color: $green;
    margin-bottom: 20px;
  }

  .create-button {
    display: inline-block;
    margin-top: 7px;
  }

  .event-sidebar {
    h2 {
      @media (min-width: $xl-min) {
        font-size: 22px;
        margin-top: 6px;
      }
    }

    .pills {
      text-align: right;

      @media (max-width: $lg-max) {
        text-align: left;
      }

      li {
        white-space: nowrap;
      }
    }
  }

  .attending {
    label {
      @include label;

      display: block;
      margin-bottom: 10px;
    }

    .text-btn {
      @include label;

      border: none;
      background: none;
      color: $green;
      padding: 0;
      outline: none;
      height: auto;
      font-weight: normal;
      text-align: right;
      text-transform: none;
      opacity: 0.5;

      &:hover,
      &:focus {
        opacity: 1;
        text-decoration: underline;
      }
    }
  }

  .avatars {
    display: inline-block;
    margin-top: 10px;
    margin-left: -5px;
    margin-bottom: -5px;

    .user-avatar {
      @include background-cover;
      @include border-radius(100px);

      background-image: url('~@/assets/img/default-user.jpg');
      display: inline-block;
      height: 50px;
      width: 50px;
      margin: 5px;
    }
  }

  .event-settings {
    label {
      @include label;

      color: $green;
      margin-bottom: 15px;
    }
  }

  .delete-btn {
    width: 100%;

    i,
    svg {
      margin-right: 5px;
    }

    &:hover,
    &:focus {
      background: $red;
      color: white;
      border-color: $red;
    }
  }
}
</style>
